/**
 * Centralized error handler to sanitize Supabase errors
 * Prevents database structure leakage to end users
 */

export function sanitizeSupabaseError(error: unknown): string {
  const msg = (error as { message?: string })?.message || 'Erro desconhecido';
  
  // RLS and permission errors
  if (msg.includes('row-level security') || msg.includes('policy') || msg.includes('permission denied')) {
    return 'Você não tem permissão para esta ação.';
  }
  
  // Duplicate/unique constraint errors
  if (msg.includes('duplicate key') || msg.includes('already exists') || msg.includes('unique constraint')) {
    return 'Já existe um registro com estas informações.';
  }
  
  // Foreign key constraint errors
  if (msg.includes('foreign key') || msg.includes('violates foreign key')) {
    return 'Este registro está sendo usado e não pode ser removido.';
  }
  
  // Check constraint errors
  if (msg.includes('check constraint') || msg.includes('violates check')) {
    return 'Dados inválidos fornecidos.';
  }
  
  // Not found errors
  if (msg.includes('No rows') || msg.includes('not found') || msg.includes('PGRST116')) {
    return 'Registro não encontrado.';
  }
  
  // Network/connection errors (including CORS)
  if (msg.includes('network') || msg.includes('fetch') || msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('CORS')) {
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  }
  
  // Auth errors
  if (msg.includes('JWT') || msg.includes('token') || msg.includes('expired')) {
    return 'Sua sessão expirou. Faça login novamente.';
  }
  
  // Email already registered
  if (msg.includes('already been registered') || msg.includes('email_exists')) {
    return 'Este email já está cadastrado no sistema.';
  }
  
  // Invalid credentials
  if (msg.includes('Invalid login') || msg.includes('credentials')) {
    return 'Email ou senha incorretos.';
  }
  
  // Log the original error internally for debugging (only in development)
  if (import.meta.env.DEV) {
    console.error('[Supabase Error - DEV]', error);
  }
  
  // Generic fallback - never expose raw database errors
  return 'Ocorreu um erro. Tente novamente.';
}

/**
 * Wrapper to handle async operations with error sanitization
 */
export async function withSanitizedError<T>(
  operation: () => Promise<T>,
  onError?: (sanitizedMessage: string, originalError: unknown) => void
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    const sanitized = sanitizeSupabaseError(error);
    onError?.(sanitized, error);
    throw new Error(sanitized);
  }
}
